import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";
import {
  deriveDefaultRegister,
  isDeclaredUserType,
  isTerminologyRegister,
  resolveActiveRegister,
  type DeclaredUserType,
  type TerminologyRegister,
} from "@/lib/terminologyRegister";
import { resolveServiceTier, type ServiceTier } from "@/lib/guidedChecklist";

type ClientPortalSession = {
  userId: string;
  clientOrganisationId: string;
  clientName: string;
  userEmail: string;
  userName: string;
  portalRole: "admin" | "editor" | "viewer";
  /** Per-user declared type (CIR-2026-544). Null only for accounts that
   * pre-date this feature — new invite-accepts always set this. */
  declaredType: DeclaredUserType | null;
  /** Derived live from declaredType (Epic 26 FR-10) — never stored. */
  defaultRegister: TerminologyRegister;
  /** Raw per-user register override (client_users.active_register), or null
   * if the user has never overridden it (CIR-2026-546, Epic 26 FR-11/FR-12). */
  activeRegisterOverride: TerminologyRegister | null;
  /** Resolved = COALESCE(activeRegisterOverride, defaultRegister).
   * PRESENTATION-ONLY (BR-05/BR-07) — selects only which content variant
   * renders; never affects progress, sequencing, or compliance data. */
  activeRegister: TerminologyRegister;
  /** client_organisations.service_tier (CIR-2026-543), defaulted to "Lite"
   * for any org with no plan assigned (Epic 1 BR-16; Epic 26 FR-14, UC-04
   * exception). PRESENTATION-ONLY — selects the support model layered on
   * the guided interface, never an access boundary (Epic 26 FR-15, FR-15a,
   * BR-04): every client user, either tier, is routed into the same engine. */
  serviceTier: ServiceTier;
};

type ClientPortalContextType = {
  session: ClientPortalSession | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  /**
   * Changes the signed-in user's declared type at any time (Epic 26 FR-11).
   * Writes directly to their own client_users row (existing self-update RLS
   * policy: auth_user_id = auth.uid() — no new trust boundary) and updates
   * the in-memory session immediately, so defaultRegister reflects the change
   * with no reload required.
   */
  updateDeclaredType: (declaredType: DeclaredUserType) => Promise<{ error?: string }>;
  /**
   * Sets (or clears, with null) the signed-in user's terminology-register
   * override at any time (Epic 26 FR-11/FR-12, BR-05, BR-07). Writes only
   * to client_users.active_register — presentation-only, never touches
   * progress, sequencing, or compliance data — and updates the in-memory
   * session immediately, mirroring updateDeclaredType.
   */
  updateActiveRegister: (
    register: TerminologyRegister | null,
  ) => Promise<{ error?: string }>;
};

const ClientPortalContext = createContext<ClientPortalContextType | undefined>(
  undefined,
);

export const useClientPortal = () => {
  const context = useContext(ClientPortalContext);
  if (!context) {
    throw new Error(
      "useClientPortal must be used within a ClientPortalProvider",
    );
  }
  return context;
};

async function loadClientSession(
  authSession: Session,
): Promise<ClientPortalSession | null> {
  const userId = authSession.user.id;

  // Look up client_users record for this auth user. declared_type and
  // active_register are not yet in the generated types (CIR-2026-544/546
  // migrations) — cast the read.
  //
  // auth_user_id has no uniqueness constraint (a person can in principle be
  // invited into more than one client organisation), so without an explicit
  // ORDER BY, Postgres may return an arbitrary matching row and the resolved
  // clientOrganisationId — which scopes every downstream portal query and
  // the access-log write — would be non-deterministic across sign-ins
  // (CIR-2026-456). Order deterministically: prefer the row where this
  // person is the org's primary contact, then the oldest membership, so a
  // multi-membership user always resolves to the same organisation.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- table not in generated types (CIR-2026-296)
  const { data: clientUser, error: cuError } = await (supabase as any)
    .from("client_users")
    .select(
      "client_organisation_id, first_name, last_name, email, portal_role, declared_type, active_register",
    )
    .eq("auth_user_id", userId)
    .order("is_primary_contact", { ascending: false })
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();

  if (cuError || !clientUser) {
    return null;
  }

  const declaredType = isDeclaredUserType(clientUser.declared_type)
    ? clientUser.declared_type
    : null;

  const activeRegisterOverride = isTerminologyRegister(clientUser.active_register)
    ? clientUser.active_register
    : null;

  // Get the client organisation name + service tier. service_tier is not yet
  // in the generated types (CIR-2026-543 migration) — cast the read.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- column not in generated types (CIR-2026-296)
  const { data: org, error: orgError } = await (supabase as any)
    .from("client_organisations")
    .select("name, service_tier")
    .eq("id", clientUser.client_organisation_id)
    .maybeSingle();

  if (orgError || !org) {
    return null;
  }

  return {
    userId,
    clientOrganisationId: clientUser.client_organisation_id,
    clientName: org.name,
    userEmail: clientUser.email ?? authSession.user.email ?? "",
    userName: [clientUser.first_name, clientUser.last_name]
      .filter(Boolean)
      .join(" "),
    portalRole:
      (clientUser.portal_role as "admin" | "editor" | "viewer") ?? "viewer",
    declaredType,
    defaultRegister: deriveDefaultRegister(declaredType),
    activeRegisterOverride,
    activeRegister: resolveActiveRegister(activeRegisterOverride, declaredType),
    serviceTier: resolveServiceTier(org.service_tier),
  };
}

export const ClientPortalProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [session, setSession] = useState<ClientPortalSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // On mount, check for an existing Supabase session
  useEffect(() => {
    let cancelled = false;

    const applySession = async (authSession: Session | null) => {
      if (!authSession) {
        if (!cancelled) {
          setSession(null);
        }
        return;
      }
      const clientSession = await loadClientSession(authSession);
      if (!cancelled) {
        setSession(clientSession);
      }
    };

    const init = async () => {
      const {
        data: { session: authSession },
      } = await supabase.auth.getSession();

      if (cancelled) return;

      await applySession(authSession);

      if (!cancelled) {
        setIsLoading(false);
      }
    };

    init();

    // Listen for auth state changes. Two things matter here (CIR-2026-454):
    //
    // 1. Supabase fires INITIAL_SESSION synchronously on subscribe with
    //    whatever session getSession() would also resolve — init() above
    //    already loads that same session, so re-loading it here would issue
    //    a duplicate client_users + client_organisations query on every
    //    mount. Skip INITIAL_SESSION; only react to real auth transitions.
    // 2. The callback itself must stay non-async and must not await Supabase
    //    calls directly — the client's internal auth lock can deadlock if a
    //    Supabase call is awaited synchronously inside this handler. Defer
    //    the actual session load to the next tick via setTimeout, matching
    //    the pattern already used in ClientContext.tsx.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, authSession) => {
      if (cancelled || event === "INITIAL_SESSION") return;

      if (event === "SIGNED_OUT") {
        setSession(null);
        return;
      }

      setTimeout(() => {
        applySession(authSession);
      }, 0);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = useCallback(
    async (
      email: string,
      password: string,
    ): Promise<{ error?: string }> => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { error: "Invalid email or password. Please try again." };
      }

      if (!data.session) {
        return { error: "Unable to establish a session. Please try again." };
      }

      // Verify this user is a client user
      const clientSession = await loadClientSession(data.session);
      if (!clientSession) {
        // Not a client user — sign them out
        await supabase.auth.signOut();
        return {
          error:
            "This account is not linked to a client organisation. Please contact your administrator.",
        };
      }

      setSession(clientSession);
      return {};
    },
    [],
  );

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
  }, []);

  // Epic 26 FR-11: change the declared type at any time; immediate effect on
  // defaultRegister via the same self-update RLS policy used at invite-accept
  // (auth_user_id = auth.uid()) — no new trust boundary.
  const updateDeclaredType = useCallback(
    async (declaredType: DeclaredUserType): Promise<{ error?: string }> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        return { error: "You must be signed in to change this." };
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- table not in generated types (CIR-2026-296)
      const { error } = await (supabase as any)
        .from("client_users")
        .update({ declared_type: declaredType })
        .eq("auth_user_id", user.id);

      if (error) {
        return { error: "Couldn't save your change. Please try again." };
      }

      setSession((prev) =>
        prev
          ? {
              ...prev,
              declaredType,
              defaultRegister: deriveDefaultRegister(declaredType),
              // Recompute in case there is no override — activeRegister must
              // track the new default immediately (Epic 26 FR-10/FR-11).
              activeRegister: resolveActiveRegister(
                prev.activeRegisterOverride,
                declaredType,
              ),
            }
          : prev,
      );
      return {};
    },
    [],
  );

  // Epic 26 FR-11/FR-12, BR-05, BR-07: set/clear the register override at any
  // time; presentation-only — writes only client_users.active_register via
  // the same self-update RLS policy as updateDeclaredType, and never touches
  // progress, sequencing, or compliance data.
  const updateActiveRegister = useCallback(
    async (
      register: TerminologyRegister | null,
    ): Promise<{ error?: string }> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        return { error: "You must be signed in to change this." };
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- column not in generated types (CIR-2026-296)
      const { error } = await (supabase as any)
        .from("client_users")
        .update({ active_register: register })
        .eq("auth_user_id", user.id);

      if (error) {
        return { error: "Couldn't save your change. Please try again." };
      }

      setSession((prev) =>
        prev
          ? {
              ...prev,
              activeRegisterOverride: register,
              activeRegister: resolveActiveRegister(register, prev.declaredType),
            }
          : prev,
      );
      return {};
    },
    [],
  );

  const value = useMemo(
    () => ({
      session,
      isLoading,
      signIn,
      signOut,
      updateDeclaredType,
      updateActiveRegister,
    }),
    [session, isLoading, signIn, signOut, updateDeclaredType, updateActiveRegister],
  );

  return (
    <ClientPortalContext.Provider value={value}>
      {children}
    </ClientPortalContext.Provider>
  );
};
